"use client";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog as DialogPrimitive, VisuallyHidden } from "radix-ui";
import {
	type ComponentProps,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Button, ScreenOverlay, Wrap, cn, useRadixUIPortalFix } from "../../..";
import { DialogContext, useDialogContext } from "./Context";

export interface DialogProps extends Omit<DialogPrimitive.DialogProps, "open" | "onOpenChange"> {
	forceOpen?: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: ReactNode;
	subtitle?: ReactNode;
	footer?: ReactNode;
	color?: "white" | "shades";
	width?: "xs" | "xl" | "2xl" | "4xl" | "5xl" | "full";
	height?: "auto" | "full";
	portal?: boolean;
	className?: string;
	innerClassName?: string;
	hideHeader?: boolean;
}

export function Dialog(props: DialogProps) {
	const {
		forceOpen: forceOpenProp = false,
		open,
		onOpenChange,
		defaultOpen,
		children,
		title,
		subtitle,
		footer,
		color = "white",
		width = "xl",
		height = "auto",
		portal = true,
		className,
		innerClassName,
		hideHeader = false,
		...restProps
	} = props;

	const [forceOpen, setForceOpen] = useState(forceOpenProp);
	const isOpen = useRadixUIPortalFix({ defaultOpen, open });

	const onOpenChangeHandler = useCallback(
		(o: boolean) => {
			if (!(forceOpenProp || forceOpen)) {
				return onOpenChange(o);
			}
		},
		[forceOpen, forceOpenProp, onOpenChange],
	);

	const modalContentInner = useRef<HTMLDivElement>(null);

	const setStyle = useCallback(() => {
		if (!modalContentInner.current) {
			setTimeout(setStyle, 100);
			return;
		}

		const element = modalContentInner.current;
		const headerHeight =
			element.previousElementSibling instanceof HTMLElement
				? element.previousElementSibling.offsetHeight
				: 0;

		if (headerHeight && !hideHeader) {
			element.style.setProperty("--modal-header-y", `${headerHeight}px`);
		}

		const footerHeight =
			element.nextElementSibling instanceof HTMLElement
				? element.nextElementSibling.offsetHeight
				: 0;

		if (footerHeight) {
			element.style.setProperty("--modal-footer-y", `${footerHeight}px`);
		}

		return () => {
			element.style.removeProperty("--modal-header-y");
			element.style.removeProperty("--modal-footer-y");
		};
	}, [hideHeader]);

	useEffect(() => {
		if (isOpen) setStyle();
	}, [isOpen, setStyle]);

	const titleComponent = (
		<DialogPrimitive.Title className="font-bold font-primary text-lg">
			{title}
		</DialogPrimitive.Title>
	);

	return (
		<DialogContext.Provider
			value={{ open: isOpen, onOpenChange: onOpenChangeHandler, forceOpen, setForceOpen }}
		>
			<DialogPrimitive.Root
				open={isOpen}
				onOpenChange={(o) => onOpenChangeHandler(o)}
				{...restProps}
			>
				<Wrap
					if={portal}
					with={(children) => <DialogPrimitive.Portal>{children}</DialogPrimitive.Portal>}
				>
					<DialogPrimitive.Overlay
						asChild
						onPointerDown={(e) => {
							if (!e.currentTarget.nextElementSibling?.ariaHidden) {
								onOpenChangeHandler(false);
							}
						}}
					>
						<ScreenOverlay variant="state" />
					</DialogPrimitive.Overlay>

					<DialogPrimitive.Content
						onPointerDownOutside={(e) => e.preventDefault()}
						className={cn(
							"fixed bottom-[env(safe-area-inset-bottom,0px)] z-dialog max-h-[calc(100%-2rem)] w-full shadow-lg focus-visible:outline-none sm:bottom-auto sm:w-[calc(100%-2rem)]",
							"sm:-translate-x-1/2 sm:-translate-y-1/2 rdx-state-closed:animate-mobileDialogOut rdx-state-open:animate-mobileDialogIn sm:top-1/2 sm:left-1/2 sm:origin-top-left sm:rdx-state-closed:animate-dialogOut sm:rdx-state-open:animate-dialogIn",
							{
								"bg-white": color === "white",
								"bg-shades-light": color === "shades",
								"rounded-t sm:rounded-b": width !== "full",
								"sm:max-w-xs": width === "xs",
								"sm:max-w-xl": width === "xl",
								"sm:max-w-2xl": width === "2xl",
								"sm:max-w-4xl": width === "4xl",
								"lg:max-w-5xl": width === "5xl",
								"h-[calc(100%-2rem)]": height === "full",
							},

							className,
						)}
					>
						{hideHeader && !forceOpen && (
							<DialogPrimitive.Close className="sm:-top-2 sm:-right-2 absolute top-2 right-2 z-1 flex h-8 w-8 items-center justify-center rounded bg-shades-light enter:ring-1 enter:ring-dark focus:outline-none motion-safe:transition sm:bg-white">
								<FontAwesomeIcon icon={faTimes} size="lg" />
							</DialogPrimitive.Close>
						)}

						<Wrap
							if={!!hideHeader}
							with={(children) => <VisuallyHidden.Root>{children}</VisuallyHidden.Root>}
						>
							<div className="flex flex-column items-baseline p-4 pb-0">
								<div className="mr-2 flex flex-col gap-2">
									{title ? (
										titleComponent
									) : (
										<VisuallyHidden.Root asChild>{titleComponent}</VisuallyHidden.Root>
									)}

									{subtitle ? (
										typeof subtitle === "string" ? (
											<DialogPrimitive.Description className="text-shades-dark">
												{subtitle}
											</DialogPrimitive.Description>
										) : (
											<DialogPrimitive.Description asChild>{subtitle}</DialogPrimitive.Description>
										)
									) : (
										<VisuallyHidden.Root asChild>
											<DialogPrimitive.Description asChild={typeof title !== "string"}>
												{title}
											</DialogPrimitive.Description>
										</VisuallyHidden.Root>
									)}
								</div>

								{!(forceOpenProp || forceOpen) && (
									<DialogPrimitive.Close className="ml-auto focus:outline-none focus-visible:text-primary-medium motion-safe:transition-colors">
										<FontAwesomeIcon icon={faTimes} size="lg" />
									</DialogPrimitive.Close>
								)}
							</div>
						</Wrap>

						<div
							ref={modalContentInner}
							className={cn(
								"scrollbar-none my-4 max-h-[calc(var(--window-y,100dvh)-var(--modal-header-y,0px)-var(--modal-footer-y,0px)-var(--spacing))] overflow-auto px-4",
								{
									"[--spacing:4rem]": height !== "full",
									"h-[calc(100%-2rem)] [--spacing:4rem]": height === "full" && !hideHeader,
									"h-full [--spacing:2rem]": height === "full" && hideHeader,
									"mt-0 mb-0": !!hideHeader,
								},
								innerClassName,
							)}
						>
							{children}
						</div>

						{!!footer && <div className="p-4 pt-0">{footer}</div>}
					</DialogPrimitive.Content>
				</Wrap>
			</DialogPrimitive.Root>
		</DialogContext.Provider>
	);
}

type CloseDialogButtonProps = ComponentProps<typeof Button>;

export function CloseDialogButton(props: CloseDialogButtonProps) {
	const dialog = useDialogContext();

	return <Button onClick={() => dialog?.onOpenChange?.(false)} {...props} />;
}
